// T4-VIEWS-MEDIA: what a view's cards and calendar chips show, and which date a calendar or
// timeline places tasks by. A view names its own card fields (`view.card_fields`); a view that
// names none falls back to the fields marked "On cards" (`field_rule.show_on_card`, Work OS's
// field-wide showOnCard), and a board with neither keeps the older chips (every single-choice
// field). Any date can drive a calendar: `due_at` (the default), `start_at` or a date field.

import { dueInstant, localDay, PRIORITIES, startInstant } from "./model";
import { fieldText } from "./task-page/fields";
import type { FieldRule } from "./task-page/queries";
import type { Board, FieldDef, TaskCard, View } from "./view-engine";

/** The built-in columns a card can show (`title` is the card itself). */
export const CARD_BUILTINS: { ref: string; label: string }[] = [
  { ref: "status", label: "Status" },
  { ref: "priority", label: "Priority" },
  { ref: "assignee", label: "Assignee" },
  { ref: "due_at", label: "Due date" },
  { ref: "start_at", label: "Start date" },
  { ref: "created_at", label: "Created" },
  { ref: "completed_at", label: "Completed" },
  { ref: "number", label: "Number" },
];

const fieldId = (ref: string) => /^field:([0-9a-f-]{36})$/.exec(ref)?.[1];

/** A card field's name ("Due date", or the board field's name); null when it is gone. */
export function cardFieldName(ref: string, fields: readonly FieldDef[]): string | null {
  const id = fieldId(ref);
  if (id) return fields.find((f) => f.id === id)?.name ?? null;
  return CARD_BUILTINS.find((b) => b.ref === ref)?.label ?? null;
}

/**
 * The view's card fields, in order (fields deleted since drop out), or null: no choice anywhere,
 * so the cards keep their older chips.
 */
export function effectiveCardFields(
  view: Pick<View, "card_fields"> | null | undefined,
  fields: readonly FieldDef[],
  rules: readonly FieldRule[] | undefined,
): string[] | null {
  if (view?.card_fields) return view.card_fields.filter((r) => cardFieldName(r, fields) !== null);
  const marked = new Set((rules ?? []).filter((r) => r.show_on_card).map((r) => r.field_def_id));
  const fallback = fields.filter((f) => marked.has(f.id)).map((f) => `field:${f.id}`);
  return fallback.length ? fallback : null;
}

const shortDay = (day: string) => {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1).toLocaleDateString([], {
    day: "numeric",
    month: "short",
  });
};

/** A stored date (a plain day or an instant) as a local YYYY-MM-DD; null when unreadable. */
export function dayOf(value: unknown): string | null {
  if (typeof value !== "string" || !value) return null;
  // A date field holds a calendar day: its first ten characters, as the field editor reads it
  // (a value stored as an instant at midnight UTC must not slip to the day before).
  const day = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

/** One card field's value as short words; null when the task has none. */
export function cardFieldText(
  card: TaskCard,
  ref: string,
  ctx: {
    fields: readonly FieldDef[];
    board?: Pick<Board, "status_set">;
    seat: (id: string) => string;
  },
): string | null {
  const id = fieldId(ref);
  if (id) {
    const f = ctx.fields.find((x) => x.id === id);
    const v = card.fields[id];
    if (!f || v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length))
      return null;
    if (f.type === "checkbox") return v === true ? f.name : null;
    if (f.type === "date") {
      const day = dayOf(v);
      return day ? `${f.name} ${shortDay(day)}` : null;
    }
    const text = fieldText(f, v, ctx.seat);
    return f.type === "select" || f.type === "multi_select" || f.type === "seat"
      ? text
      : `${f.name}: ${text}`;
  }
  switch (ref) {
    case "status":
      return ctx.board?.status_set.find((s) => s.key === card.status)?.name ?? card.status;
    case "priority":
      return card.priority
        ? (PRIORITIES.find((p) => p.value === card.priority)?.label ?? null)
        : null;
    case "assignee":
      return card.assignee_seat_ids.length ? card.assignee_seat_ids.map(ctx.seat).join(", ") : null;
    case "due_at":
      return card.due_at ? `Due ${shortDay(localDay(card.due_at))}` : null;
    case "start_at":
      return card.start_at ? `Starts ${shortDay(localDay(card.start_at))}` : null;
    case "created_at":
      return `Created ${shortDay(localDay(card.created_at))}`;
    case "completed_at":
      return card.completed_at ? `Done ${shortDay(localDay(card.completed_at))}` : null;
    case "number":
      return `#${card.number}`;
    default:
      return null;
  }
}

/** The card's field texts for these refs, skipping empty ones, at most `max`. */
export function cardFieldTexts(
  card: TaskCard,
  refs: readonly string[],
  ctx: Parameters<typeof cardFieldText>[2],
  max: number,
): string[] {
  const out: string[] = [];
  for (const r of refs) {
    if (out.length >= max) break;
    const t = cardFieldText(card, r, ctx);
    if (t) out.push(t);
  }
  return out;
}

// Calendar dates ---------------------------------------------------------------------------------

/** The dates a calendar or timeline can run on: due, start, and the board's date fields. */
export function dateFieldChoices(fields: readonly FieldDef[]): { ref: string; label: string }[] {
  return [
    { ref: "due_at", label: "Due date" },
    { ref: "start_at", label: "Start date" },
    ...fields
      .filter((f) => f.type === "date")
      .map((f) => ({ ref: `field:${f.id}`, label: f.name })),
  ];
}

/** The view's calendar date, `due_at` when it names none or names a field deleted since. */
export function viewDateField(
  view: Pick<View, "date_field"> | null | undefined,
  fields: readonly FieldDef[],
): string {
  const ref = view?.date_field ?? "due_at";
  const id = fieldId(ref);
  if (id && !fields.some((f) => f.id === id && f.type === "date")) return "due_at";
  return ref;
}

/** The task's day on that date (YYYY-MM-DD), or null. */
export function taskDay(card: TaskCard, ref: string): string | null {
  if (ref === "due_at") return card.due_at ? localDay(card.due_at) : null;
  if (ref === "start_at") return card.start_at ? localDay(card.start_at) : null;
  const id = fieldId(ref);
  return id ? dayOf(card.fields[id]) : null;
}

/** The write that puts the task on `day` for that date. */
export function dayWrite(
  ref: string,
  day: string,
):
  | { kind: "update"; body: { due_at: string } | { start_at: string } }
  | { kind: "field"; field: string; value: string } {
  if (ref === "start_at") return { kind: "update", body: { start_at: startInstant(day) } };
  const id = fieldId(ref);
  if (id) return { kind: "field", field: id, value: day };
  return { kind: "update", body: { due_at: dueInstant(day) } };
}
